import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, CheckCircle, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SettingsPage = () => {
  const [settings, setSettings] = useState({ notification_email: '', email_enabled: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/settings`, { headers: getHeaders() });
      setSettings(data);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings`, settings, { headers: getHeaders() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-sm text-slate-400 mt-1">Configure your site settings</p>
      </div>

      <div className="space-y-6">
        {/* Email Notifications */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-50 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="w-4 h-4 text-blue-500" />
            </div>
            <h2 className="font-semibold text-slate-700">Email Notifications</h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">Notification Email</label>
              <input
                data-testid="notification-email-input"
                type="email"
                value={settings.notification_email || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, notification_email: e.target.value }))}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50/50 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                placeholder="admin@nexa.sa"
              />
              <p className="text-xs text-slate-400 mt-1.5">You'll receive email notifications when someone submits the contact form</p>
            </div>

            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
              <div className={`w-3 h-3 rounded-full ${settings.email_enabled ? 'bg-emerald-400' : 'bg-slate-300'}`} />
              <span className="text-sm text-slate-600">
                Email notifications are {settings.email_enabled ? 'enabled' : 'disabled'}
              </span>
              {!settings.email_enabled && (
                <span className="text-xs text-slate-400">(Requires Resend API key in backend .env)</span>
              )}
            </div>

            <button
              data-testid="save-settings-btn"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm px-6 py-2.5 rounded-xl transition-colors disabled:opacity-60 shadow-lg shadow-blue-500/20"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><CheckCircle className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save Settings</>}
            </button>
          </div>
        </div>

        {/* API Status */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-50">
            <h2 className="font-semibold text-slate-700">System Info</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Backend</p>
                <div className="text-sm font-medium text-emerald-600 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> Running
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Database</p>
                <div className="text-sm font-medium text-emerald-600 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> Connected
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
